import { useState } from "react";
import { ChevronRight, Pencil, Save, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/useAuth";
import { type Stage, getStage, putOverride } from "../lib/overlay";
import { getTags, nextEngagementWrap } from "../lib/queries";
import { TagEditor } from "./TagEditor";
import type { Project } from "../types";

const eqTags = (a: string[], b: string[]) => JSON.stringify(a) === JSON.stringify(b);

const STAGES: Stage[] = ["draft", "in-review", "published", "archived"];
const STAGE_LABEL: Record<Stage, string> = {
  draft: "Draft",
  "in-review": "In review",
  published: "Published",
  archived: "Archived",
};

/** Admin-only affordance on an engagement page: an Edit button that expands the inline editor. */
export function AdminEdit({ project }: { project: Project }) {
  const { isAdmin } = useAuth();
  const [editing, setEditing] = useState(false);
  if (!isAdmin) return null;
  return editing ? (
    <EngagementEditor project={project} onClose={() => setEditing(false)} />
  ) : (
    <div className="eng-edit-bar">
      <span className="eng-stage-badge mono" data-stage={getStage(project.id, project.status)}>
        {STAGE_LABEL[getStage(project.id, project.status)]}
      </span>
      <button type="button" className="btn btn-sm" onClick={() => setEditing(true)}>
        <Pencil size={14} /> Edit engagement
      </button>
    </div>
  );
}

function EngagementEditor({ project, onClose }: { project: Project; onClose: () => void }) {
  const { getApiToken } = useAuth();
  const navigate = useNavigate();
  const [title, setTitle] = useState(project.title);
  const [summary, setSummary] = useState(project.summary);
  const [narrative, setNarrative] = useState(project.narrative ?? "");
  const [stage, setStage] = useState<Stage>(getStage(project.id, project.status));
  const [industries, setIndustries] = useState<string[]>(project.industries);
  const [technologies, setTechnologies] = useState<string[]>(project.technologies);
  const [skills, setSkills] = useState<string[]>(project.skills);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const doSave = async (): Promise<boolean> => {
    setSaving(true);
    setErr(null);
    const token = await getApiToken();
    if (!token) {
      setErr("Your sign-in session expired — please sign in again.");
      setSaving(false);
      return false;
    }
    const fields: Record<string, string> = {};
    if (title !== project.title) fields.title = title;
    if (summary !== project.summary) fields.summary = summary;
    if (narrative !== (project.narrative ?? "")) fields.narrative = narrative;
    if (!eqTags(industries, project.industries)) fields.industries = JSON.stringify(industries);
    if (!eqTags(technologies, project.technologies)) fields.technologies = JSON.stringify(technologies);
    if (!eqTags(skills, project.skills)) fields.skills = JSON.stringify(skills);
    const ok = await putOverride(project.id, { stage, fields }, token);
    setSaving(false);
    if (!ok) setErr("Save failed — your session may have expired. Sign out, sign back in, and retry.");
    return ok;
  };
  const save = async () => {
    if (await doSave()) onClose();
  };
  const saveAndNext = async () => {
    if (!(await doSave())) return;
    const nx = nextEngagementWrap(project.id);
    if (nx && nx.id !== project.id) navigate(`/project/${nx.id}`);
    else onClose();
  };

  return (
    <div className="eng-editor card card-pad">
      <div className="eng-editor-head">
        <strong>Edit engagement</strong>
        <label className="eng-stage-pick mono">
          Stage
          <select value={stage} onChange={(e) => setStage(e.target.value as Stage)}>
            {STAGES.map((s) => (
              <option key={s} value={s}>
                {STAGE_LABEL[s]}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="icon-btn" onClick={onClose} aria-label="Close">
          <X size={16} />
        </button>
      </div>
      <label className="eng-field">
        <span>Title</span>
        <input value={title} onChange={(e) => setTitle(e.target.value)} />
      </label>
      <label className="eng-field">
        <span>Summary</span>
        <textarea rows={2} value={summary} onChange={(e) => setSummary(e.target.value)} />
      </label>
      <label className="eng-field">
        <span>Narrative</span>
        <textarea rows={6} value={narrative} onChange={(e) => setNarrative(e.target.value)} />
      </label>
      <div className="eng-tags">
        <TagEditor label="Industries" singular="industry" value={industries} options={getTags("industry")} onChange={setIndustries} />
        <TagEditor label="Technologies" singular="technology" value={technologies} options={getTags("technology")} onChange={setTechnologies} />
        <TagEditor label="Skills" singular="skill" value={skills} options={getTags("skill")} onChange={setSkills} />
      </div>
      {err && <p className="eng-err">{err}</p>}
      <div className="eng-actions">
        <button type="button" className="btn btn-sm" onClick={onClose} disabled={saving}>
          Cancel
        </button>
        <button type="button" className="btn btn-sm" onClick={saveAndNext} disabled={saving}>
          <ChevronRight size={14} /> Save &amp; Next
        </button>
        <button type="button" className="btn btn-sm btn-primary" onClick={save} disabled={saving}>
          <Save size={14} /> {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}
